import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  MailCheck,
  RefreshCw,
  ShieldCheck,
  Timer,
} from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "../components/AuthLayout";
import { Button, ButtonLink } from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { api, ApiError } from "../lib/api";
import {
  clearOtpChallenge,
  getOtpChallenge,
  rememberOtpChallenge,
} from "../lib/otpChallenge";
import { Redirect, useRouter } from "../lib/router";
import { takeReturnPath } from "../lib/returnPath";
import type { OtpChallenge } from "../types/api";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

function remainingSeconds(expiresAt: string) {
  const expiration = new Date(expiresAt).getTime();
  if (Number.isNaN(expiration)) return 0;
  return Math.max(Math.floor((expiration - Date.now()) / 1000), 0);
}

function formatCountdown(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

export function LoginOtpPage() {
  const { confirmOtp, isAuthenticated } = useAuth();
  const { navigate } = useRouter();
  const [challenge, setChallenge] = useState<OtpChallenge | null>(() =>
    getOtpChallenge(),
  );
  const [digits, setDigits] = useState<string[]>(() => Array(CODE_LENGTH).fill(""));
  const [trustDevice, setTrustDevice] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [expiresIn, setExpiresIn] = useState(() =>
    challenge ? remainingSeconds(challenge.expiraEm) : 0,
  );
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_SECONDS);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const submittingRef = useRef(false);

  const code = useMemo(() => digits.join(""), [digits]);
  const codeIsComplete = code.length === CODE_LENGTH;

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (!challenge) return undefined;
    const timer = window.setInterval(() => {
      setExpiresIn(remainingSeconds(challenge.expiraEm));
      setResendIn((current) => (current > 0 ? current - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [challenge]);

  if (isAuthenticated) return <Redirect to="/" />;
  if (!challenge) return <Redirect to="/login" />;

  function restartLogin(message: string) {
    clearOtpChallenge();
    setChallenge(null);
    toast.error(message);
    navigate("/login", { replace: true });
  }

  function fillDigits(value: string, startIndex: number) {
    const characters = value.replace(/\D/g, "").split("");
    if (characters.length === 0) return;

    const next = [...digits];
    characters.forEach((character, offset) => {
      const position = startIndex + offset;
      if (position < CODE_LENGTH) next[position] = character;
    });

    setDigits(next);
    setError("");
    inputsRef.current[
      Math.min(startIndex + characters.length, CODE_LENGTH - 1)
    ]?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>, index: number) {
    if (event.key === "Backspace") {
      event.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = "";
      } else if (index > 0) {
        next[index - 1] = "";
        inputsRef.current[index - 1]?.focus();
      }
      setDigits(next);
      setError("");
      return;
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      event.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>, index: number) {
    event.preventDefault();
    fillDigits(event.clipboardData.getData("text"), index);
  }

  async function validateCode(typedCode: string) {
    if (submittingRef.current) return;
    if (typedCode.length !== CODE_LENGTH) {
      setError("Preencha o código de 6 dígitos.");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    setError("");
    try {
      await confirmOtp(challenge!.desafio, typedCode, trustDevice);
      navigate(takeReturnPath(), { replace: true });
    } catch (validationError) {
      setDigits(Array(CODE_LENGTH).fill(""));
      inputsRef.current[0]?.focus();

      if (validationError instanceof ApiError && validationError.status === 410) {
        restartLogin(validationError.message);
        return;
      }
      if (validationError instanceof ApiError && validationError.status === 429) {
        setError("Muitas tentativas. Aguarde um minuto e tente novamente.");
        return;
      }
      if (validationError instanceof ApiError && validationError.status === 400) {
        const remaining = (validationError.data as { tentativasRestantes?: number })
          ?.tentativasRestantes;
        setError(
          remaining && remaining > 0
            ? `Código inválido. ${remaining === 1 ? "Resta 1 tentativa" : `Restam ${remaining} tentativas`}.`
            : "Código inválido ou expirado.",
        );
        return;
      }
      setError("Não foi possível validar o código. Verifique sua conexão.");
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  }

  async function resendCode() {
    if (resendIn > 0 || resending) return;

    setResending(true);
    setError("");
    try {
      const renewed = await api.resendLoginOtp(challenge!.desafio);
      rememberOtpChallenge(renewed);
      setChallenge(renewed);
      setDigits(Array(CODE_LENGTH).fill(""));
      setExpiresIn(remainingSeconds(renewed.expiraEm));
      setResendIn(RESEND_COOLDOWN_SECONDS);
      inputsRef.current[0]?.focus();
      toast.success("Enviamos um novo código para o seu e-mail.");
    } catch (resendError) {
      if (resendError instanceof ApiError && resendError.status === 410) {
        restartLogin(resendError.message);
        return;
      }
      if (resendError instanceof ApiError && resendError.status === 429) {
        setResendIn(RESEND_COOLDOWN_SECONDS);
        setError("Você já pediu vários códigos. Aguarde alguns minutos.");
        return;
      }
      setError("Não foi possível reenviar o código. Tente novamente.");
    } finally {
      setResending(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void validateCode(code);
  }

  return (
    <AuthLayout>
      <ButtonLink to="/login" variant="ghost" size="sm" className="auth-back-link">
        <ArrowLeft />
        Voltar
      </ButtonLink>
      <p className="eyebrow">Verificação em duas etapas</p>
      <h2>Confirme seu acesso</h2>
      <p className="login-subtitle">
        Enviamos um código de 6 dígitos para <strong>{challenge.email}</strong>.
        Ele vale por 15 minutos e pode ser usado uma única vez.
      </p>

      <form onSubmit={submit} className="login-form">
        <div
          className="otp-inputs"
          role="group"
          aria-label="Código de verificação de 6 dígitos"
        >
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(element) => {
                inputsRef.current[index] = element;
              }}
              className="otp-digit"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={1}
              value={digit}
              aria-label={`Dígito ${index + 1}`}
              aria-invalid={Boolean(error)}
              disabled={loading}
              onChange={(event) => fillDigits(event.target.value, index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              onPaste={(event) => handlePaste(event, index)}
              onFocus={(event) => event.target.select()}
            />
          ))}
        </div>

        <p className="otp-expiry">
          <Timer />
          {expiresIn > 0
            ? `O código expira em ${formatCountdown(expiresIn)}`
            : "O código expirou. Solicite um novo."}
        </p>

        {error && <p className="auth-form-error" role="alert">{error}</p>}

        <label className="otp-remember">
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(event) => setTrustDevice(event.target.checked)}
          />
          <span>
            <strong>Confiar neste dispositivo</strong>
            <small>Não pediremos o código por 30 dias neste navegador.</small>
          </span>
        </label>

        <Button
          type="submit"
          size="lg"
          loading={loading}
          disabled={!codeIsComplete}
          className="w-full"
        >
          Confirmar e entrar
          {!loading && <ArrowRight />}
        </Button>
      </form>

      <div className="otp-resend">
        <MailCheck />
        <span>Não recebeu o código?</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          loading={resending}
          disabled={resendIn > 0}
          onClick={resendCode}
        >
          {!resending && <RefreshCw />}
          {resendIn > 0 ? `Reenviar em ${resendIn}s` : "Reenviar código"}
        </Button>
      </div>

      <div className="access-notice access-notice--muted">
        <ShieldCheck />
        <span>
          Nunca compartilhe este código. A equipe Mira não solicita códigos de
          acesso por telefone ou mensagem.
        </span>
      </div>
    </AuthLayout>
  );
}
